<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import { RouterView, useRoute } from "vue-router"

import { useI18nStore } from "@/stores/i18n"
import { useConfigStore } from "@/stores/config"
import { useUserStore } from "@/stores/user"
import { useBranding } from "@/composables/useBranding"

import Background from "@/components/af/Background/Background.vue"
import ShortCut from "@/components/dev/ShortCut.vue"
import PreviousStateModal from "@/components/af/PreviousStateModal/PreviousStateModal.vue"
import LeaveAppModal from "@/components/af/LeaveAppModal/LeaveAppModal.vue"
import ExitIframeModal from "@/components/af/ExitIframeModal/ExitIframeModal.vue"
// import HelpAudioModal from "@/components/af/Help/HelpAudioModal.vue"
import LeaveWindowModal from "@/components/af/LeaveWindowModal/LeaveWindowModal.vue"

import HeaderControls from "@/components/af/HeaderControls/HeaderControls.vue"

const userStore = useUserStore()
const { getUserUuid, isResellerAcc } = storeToRefs(userStore)
const configStore = useConfigStore()

const { currentLocale } = storeToRefs(useI18nStore())
const { appReady } = storeToRefs(useConfigStore())
const { applyBrandingStyle, applyBrandingFavicon, applyUnbrandedTitle } =
  useBranding()

// Watch user and update the favicon once the reseller user is available.
watch(
  isResellerAcc,
  (isReseller) => {
    if (isReseller) {
      applyBrandingFavicon()
      applyUnbrandedTitle()
    }
  },
  { immediate: true }
)

const isMounted = ref(false)
onMounted(async () => {
  applyBrandingStyle()

  setTimeout(() => {
    isMounted.value = true
  }, 2000)
})

// const devMode = process.env.NODE_ENV === "development"

// Check for iframe
function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    // Si l'accès à window.top est bloqué (cross-origin), on est dans une iframe
    return true
  }
}

const route = useRoute()
const path = computed(() => route.path)

const isDevModeSkipAssessment = computed(() => {
  const isDevEnv =
    configStore.environment !== "production" ||
    configStore.urls.app.includes("simu.assessfirst.com")
  const isAssessmentSkipable = [
    "shape",
    "swipe",
    "drive",
    "brain",
    "move",
  ].some((assessment) => path.value.includes(assessment))
  return isDevEnv && isAssessmentSkipable
})
</script>

<template>
  <!-- background -->
  <Background />

  <!-- dev utils -->
  <!-- <DevBox v-if="devMode" /> -->

  <!-- Swipe: Quick actions -->
  <ShortCut v-if="isDevModeSkipAssessment" />

  <!-- app content is reactive to current locale -->
  <main
    :class="[
      'text-afbranding-text relative flex min-h-dvh w-full flex-col items-center justify-start lg:justify-center lg:py-8',
      currentLocale.iso_code_639 === 'ar-SA' && 'arabic',
    ]"
    :key="currentLocale.iso_code_639"
  >
    <!-- header bar with lang, help, etc -->
    <div
      class="container mt-0 flex justify-between py-5 lg:absolute lg:top-0 lg:p-8"
    >
      <!-- box to teleports stuffs -->
      <div id="box-heading"></div>

      <div
        :class="[
          'flex items-start gap-2',
          currentLocale.iso_code_639 === 'ar-SA' ? 'mr-auto' : 'ml-auto',
        ]"
      >
        <!-- Top right controls -->
        <HeaderControls v-if="appReady" />
      </div>
    </div>

    <!-- main view -->
    <RouterView v-slot="{ Component }">
      <Transition
        mode="out-in"
        enter-active-class="duration-300 ease-out"
        enter-from-class="transform opacity-0 -translate-x-3"
        enter-to-class="opacity-100 translate-x-0"
        leave-active-class="duration-300 ease-in"
        leave-from-class="opacity-100 translate-x-0"
        leave-to-class="transform opacity-0 translate-x-3"
      >
        <Component :is="Component" />
      </Transition>
    </RouterView>

    <!-- modal to handle audio -->
    <!-- <HelpAudioModal /> -->
    <!-- modal to handle and confirm previous action -->
    <PreviousStateModal />
    <!-- modal to leaving the App-->
    <LeaveAppModal />
    <!-- modal to trigger leaving window -->
    <LeaveWindowModal v-if="path.includes('voice/test')" />
    <!-- modal to leave app in new tab because of iframe -->
    <ExitIframeModal v-if="isMounted && isInIframe() && !getUserUuid" />
  </main>
</template>
